import React, { useMemo } from "react";

import { sortBy } from "lodash";

import {
  Container,
  ScreenContainer,
  ScreenHeader,
  Link as WombatLink,
  EmptyState,
  Link,
} from "../components/wombat-ui";
import { ReviewIcon } from "../components/icons";

import useTranslations from "../hooks/useTranslations";
import useViewer from "../hooks/useViewer";
import FollowUpCard from "../components/reviews/FollowUpCard";
import List from "../components/utils/List";
import { ReviewFragment, useGetReviews } from '../graphql/generated/definitions';

export type ReviewType = "all" | "next" | "ongoing" | "finished" | "available";

// FIXME: Relocate MAdUtilities
export function getReviewStatus(review: ReviewFragment): ReviewType {
  if (!review.collaborator) return "available";
  else if (new Date(review.startDate) > new Date()) {
    return "next";
  } else if (
    (new Date(review.startDate) < new Date() &&
      new Date(review.endDate) > new Date()) ||
    (review.schedule && !review.endDate)
  ) {
    return "ongoing";
  } else if (new Date(review.endDate) < new Date()) {
    return "finished";
  }
}

type ReviewListScreenProps = {
  location: { pathname: string };
};

const ReviewListScreen: React.FC<ReviewListScreenProps> = () => {
  const t = useTranslations();
  const { isCapableOfInsertingReviews, id: viewerId } = useViewer();

  const { data, loading } = useGetReviews({
    variables: { sortBy: "createdAt", direction: "DESC" },
  });
  const reviews = useMemo(() => {
    if (data && data?.reviews.collection) {
      const sortedDates = sortBy(data?.reviews.collection, [
        function (review) {
          return new Date(review.createdAt);
        },
      ]);
      return sortedDates;
    }
    return data?.reviews?.collection || [];
  }, [data]);

  return (
    <ScreenContainer>
      <ScreenHeader
        title={t("reviews.reviews") as string}
        description={t("reviews.reviewsDescription") as string}
        icon={ReviewIcon}
        actions={[
          isCapableOfInsertingReviews && (
            <WombatLink variant="button" to="/reviews/new" key="new">
              {t("header.createReview")}
            </WombatLink>
          ),
        ]}
      />
      <Container>
        <List
          id="review-followups-cards"
          listOnly
          renderAsList={(review) => (
            <FollowUpCard
              key={review.id}
              review={review}
              reviewType={getReviewStatus(review)}
              isCollaboratorViewing={viewerId === review.collaborator.id}
            />
          )}
          data={reviews}
          isLoading={loading}
          emptyMessage={t("reviews.noResultsWithFilters") as string}
        />
      </Container>
    </ScreenContainer>
  );
};

export default ReviewListScreen;
